import {Dispatch} from "redux";
import {getContainer} from "../ioc/IOCSetup";
import {ResetPasswordChannel, UserService} from "./UserService";
import {
    AccountBalance,
    AppMode,
    BalanceAndBonus,
    BonusOffer,
    KycStatus,
    LoginResponseStatus,
    LoginVerification,
    ManualLoginRequest,
    MultiFactorAuthProvider,
    NextUpcomingCashback,
    ReCaptchaAction,
    RegistrationRequest,
    RegistrationResponse,
    ResetPasswordRequest,
    Settings,
    Status,
    TokenLoginRequest,
    UserBonusView,
    UserInfo,
    UserLevelView,
    UserMessage,
    UserMessageType
} from "../http/protocol";
import {LAST_SELECTED_CURRENCY_KEY, LoginError} from "./UserReducer";
import {SubmissionError} from "redux-form";
import {t} from "../i18n";
import {AppState} from "../RootReducer";
import {deviceDetect} from 'react-device-detect';
import {BonusActionTypes} from "../bonus/BonusActions";
import {SettingsActionTypes, setBalanceUpdateFrequency, stopBalanceUpdate} from "../settings/SettingsActions";
import {executeCaptcha, isReCaptchaActionEnabled} from "../ReCaptcha";
import {onUserDeposit, onUserLogin, onUserRegistered, onUserWithdrawApproved} from "../events/UserEvents";
import { defaultBalanceUpdateFrequency } from "../ui/BalanceUpdater";
import { getAppContext } from "../selectors/settings-selectors";
import { updateCountry } from "../settings/SettingsActions";

export enum UserActionTypes {
    LOGIN_REQUEST = "@user/login/request",
    LOGIN_RESPONSE = "@user/login/response",
    MFA_VERIFICATION = "@user/login/mfa_required",
    MFA_VERIFICATION_ERROR = "@user/login/mfa_error",
    MFA_RESET = "@user/login/mfa_reset",
    LOGIN_ERROR = "@user/login/error",
    TERMS_VERSION_ACCEPT_REQUIRED = "@user/login/terms_accept_required",
    TERMS_VERSION_DECLINED = "@user/login/terms_declined",
    FACEBOOK_LOGIN_START = "@user/login/facebook/start",
    FACEBOOK_LOGIN_RESPONSE = "@user/login/facebook/response",
    FACEBOOK_LOGIN_CANCELED = "@user/login/facebook/canceled",
    LOGOUT = "@user/logout",
    SHOW_LOGIN = "@user/show-login",
    SHOW_MENU_MOBILE = "@user/show-menu-mobile",
    SHOW_BALANCE_DROPDOWN = "@user/show-balance-dropdown",
    BALANCE_REQUEST = "@user/balance/request",
    BALANCE_RESPONSE = "@user/balance/response",
    BALANCE_ERROR = "@user/balance/error",
    USER_INFO_REQUEST = "@user/info/request",
    USER_INFO_RESPONSE = "@user/info/response",
    USER_INFO_ERROR = "@user/info/error",
    REGISTER_REQUEST = "@user/register/request",
    REGISTER_RESPONSE = "@user/register/response",
    REGISTER_ERROR = "@user/register/error",
    REQUEST_RECOVER_PASSWORD = "@user/requestresetpassword/request",
    REQUEST_RECOVER_PASSWORD_ERROR = "@user/requestresetpassword/error",
    RESET_PASSWORD_REQUEST = "@user/resetpassword/request",
    RESET_PASSWORD_RESPONSE = "@user/resetpassword/response",
    RESET_PASSWORD_ERROR = "@user/resetpassword/error",
    RESET_PASSWORD_CLEAR = "@user/resetpassword/clear",
    SET_ACTIVE_CURRENCY = "@user/activeCurrency",
    SET_SHOW_BALANCE = "@user/showbalance",
    VERIFY_EMAIL_REQUEST = "@user/verifyEmail/request",
    VERIFY_EMAIL_RESPONSE = "@user/verifyEmail/response",
    VERIFY_EMAIL_ERROR = "@user/verifyEmail/error",
    VERIFY_RESET = "@user/verifyEmail/reset",
    VERIFY_CHANNEL_CODE_REQUEST = "@user/verifyChannelCode/request",
    VERIFY_CHANNEL_CODE_RESPONSE = "@user/verifyChannelCode/response",
    VERIFY_CHANNEL_CODE_ERROR = "@user/verifyChannelCode/error",
    SET_MATI_STATUS = "@user/metamapstatus/set",
    USERSETTINGS_REQUEST = "@user/updateusersettings/request",
    USERSETTINGS_RESPONSE = "@user/updateusersettings/response",
    USERSETTINGS_ERROR = "@user/updateusersettings/error",
    SET_APP_MODE = "@user/set/appMode",
    SHOW_PUSH_PROMPT = "@user/show-push-prompt",
    REDIRECT_USER = "@user/redirect",
    SHOW_IOS_INSTALL_GUIDE = "@user/show-ios-install-guide",
    SET_NEW_USER = "@user/set/new-user",
    LEVEL_VIEW_REQUEST = "@user/levelView/request",
    LEVEL_VIEW_RESPONSE = "@user/levelView/response",
    SET_HOMESCREEN = "@user/set/homescreen",
    SET_USER_COUNTRY = "@user/country",
    SET_USER_MODAL = "@user/modal",
    MANUAL_APPROVAL_REQUIRED = "@user/login/manualApprovalRequired",
    CLEAR_MANUAL_APPROVAL = "@user/login/manualApprovalClear",
    CASHBACK_REQUEST = "@user/cashback/request",
    CASHBACK_RESPONSE = "@user/cashback/response",
    CASHBACK_ERROR = "@user/cashback/error",
    TEMP = "@user/temp/metamap"
}
export interface BalanceResponse {
    balances : AccountBalance[];
    activeCurrency? : string;
    bonusOffers : BonusOffer[];
    activeBonuses : UserBonusView[];
    messages? : UserMessage[];
    unreadMessages? : number;
}

export type UserAction =
    | { type : UserActionTypes.LOGIN_REQUEST, request : ManualLoginRequest }
    | { type : UserActionTypes.SHOW_LOGIN, show : boolean }
    | { type : UserActionTypes.SHOW_MENU_MOBILE, show : boolean }
    | { type : UserActionTypes.SHOW_BALANCE_DROPDOWN, show : boolean }
    | { type : UserActionTypes.LOGIN_RESPONSE, response : UserInfo}
    | { type : UserActionTypes.LOGIN_ERROR, error: LoginError}
    | { type : UserActionTypes.LOGOUT }
    | { type : UserActionTypes.BALANCE_REQUEST}
    | { type : UserActionTypes.BALANCE_RESPONSE, response : BalanceResponse, login : boolean}
    | { type : UserActionTypes.BALANCE_ERROR, error: Error}
    | { type : UserActionTypes.USER_INFO_REQUEST}
    | { type : UserActionTypes.USER_INFO_RESPONSE, info: UserInfo}
    | { type : UserActionTypes.USER_INFO_ERROR, error: Error}
    | { type : UserActionTypes.REGISTER_REQUEST}
    | { type : UserActionTypes.REGISTER_RESPONSE, response : RegistrationResponse}
    | { type : UserActionTypes.REGISTER_ERROR, error : Error}
    | { type : UserActionTypes.REQUEST_RECOVER_PASSWORD, channel: ResetPasswordChannel, address: string}
    | { type : UserActionTypes.REQUEST_RECOVER_PASSWORD_ERROR, error : Error, channel : ResetPasswordChannel}
    | { type : UserActionTypes.RESET_PASSWORD_REQUEST, request : ResetPasswordRequest }
    | { type : UserActionTypes.RESET_PASSWORD_RESPONSE, response : any, channel : ResetPasswordChannel }
    | { type : UserActionTypes.RESET_PASSWORD_ERROR, error : Error }
    | { type : UserActionTypes.RESET_PASSWORD_CLEAR }
    | { type : UserActionTypes.SET_ACTIVE_CURRENCY, activeCurrency : string}
    | { type : UserActionTypes.SET_SHOW_BALANCE, show : boolean}
    | { type : UserActionTypes.VERIFY_EMAIL_REQUEST, newDevice?: boolean }
    | { type : UserActionTypes.VERIFY_EMAIL_RESPONSE, request : ManualLoginRequest  }
    | { type : UserActionTypes.VERIFY_EMAIL_ERROR }
    | { type : UserActionTypes.VERIFY_RESET}
    | { type : UserActionTypes.VERIFY_CHANNEL_CODE_REQUEST }
    | { type : UserActionTypes.VERIFY_CHANNEL_CODE_RESPONSE }
    | { type : UserActionTypes.VERIFY_CHANNEL_CODE_ERROR, error : Error  }
    | { type : UserActionTypes.FACEBOOK_LOGIN_START }
    | { type : UserActionTypes.FACEBOOK_LOGIN_RESPONSE, response : any}
    | { type : UserActionTypes.FACEBOOK_LOGIN_CANCELED }
    | { type : UserActionTypes.SET_MATI_STATUS, status: KycStatus }
    | { type : UserActionTypes.USERSETTINGS_REQUEST, request: string}
    | { type : UserActionTypes.USERSETTINGS_RESPONSE, response: Settings }
    | { type : UserActionTypes.USERSETTINGS_ERROR, error: Error}
    | { type : UserActionTypes.SET_APP_MODE, appMode?: AppMode}
    | { type : UserActionTypes.SHOW_PUSH_PROMPT, show: boolean}
    | { type : UserActionTypes.REDIRECT_USER, url: string}
    | { type : UserActionTypes.SHOW_IOS_INSTALL_GUIDE, show: boolean}
    | { type : UserActionTypes.MFA_VERIFICATION, provider : MultiFactorAuthProvider, username : string, password : string }
    | { type : UserActionTypes.MFA_VERIFICATION_ERROR }
    | { type : UserActionTypes.MFA_RESET }
    | { type : UserActionTypes.LEVEL_VIEW_REQUEST}
    | { type : UserActionTypes.LEVEL_VIEW_RESPONSE, response : any}
    | { type : UserActionTypes.TERMS_VERSION_ACCEPT_REQUIRED, versionToAccept: string, request: ManualLoginRequest }
    | { type : UserActionTypes.TERMS_VERSION_DECLINED }
    | { type:  UserActionTypes.SET_HOMESCREEN, homeScreen: boolean }
    | { type : UserActionTypes.MANUAL_APPROVAL_REQUIRED }
    | { type : UserActionTypes.CLEAR_MANUAL_APPROVAL }
    | { type: UserActionTypes.SET_USER_MODAL, verification: boolean, newUser: boolean }
    | { type : UserActionTypes.CASHBACK_REQUEST }
    | { type : UserActionTypes.CASHBACK_RESPONSE, response : NextUpcomingCashback[] }
    | { type : UserActionTypes.CASHBACK_ERROR, error : Error }
    | { type : UserActionTypes.TEMP, temp: any }
;

export const setTempUser = (tempUser: RegistrationRequest) => (
    { type : UserActionTypes.TEMP, temp: tempUser }
);

export const handleModalPath = (verification: boolean, newUser: boolean): UserAction =>  {
    return { type: UserActionTypes.SET_USER_MODAL, verification: verification, newUser: newUser }
}


export const setShowIOSGuide = (show:boolean): UserAction =>  {
    return{ type : UserActionTypes.SHOW_IOS_INSTALL_GUIDE, show:show};
}

export const setMetamapStatus = (status: KycStatus) => (
    { type : UserActionTypes.SET_MATI_STATUS, status: status }
);
export const setAppMode = (enabled: AppMode) => (
    { type : UserActionTypes.SET_APP_MODE, appMode: enabled}
);
// export const setHomeScreen = (enabled: boolean) => (
//     { type : UserActionTypes.SET_HOMESCREEN, homeScreen: enabled}
// )
export const loginWithFacebook = () => {
    return async (dispatch : Dispatch) => {
        dispatch({ type : UserActionTypes.FACEBOOK_LOGIN_START });
        let res = await getContainer().getFacebookApi().login();
        dispatch({ type : UserActionTypes.FACEBOOK_LOGIN_RESPONSE, response : res});
        if(res.status == "connected") {
            try {
                let response = await getContainer().getUserService().facebookLogin(res.authResponse.accessToken);

                if(response.loggedIn === false) {
                    dispatch(loginError(LoginError.FB_EMAIL_ALREADY_EXIST));
                } else {
                    response.loggedIn = true;
                    dispatch(loginResponse(response));
                    dispatch(fetchBalance(true) as any);
                }
            } catch (e: any) {
                console.log("Login Error ", e)
                dispatch(loginError(e));
            }

        } else if(res.status == "unknown") {
            dispatch({ type : UserActionTypes.FACEBOOK_LOGIN_CANCELED });
        }

    }
}

export const showMenuMobile = (show : boolean): UserAction => (
    {type : UserActionTypes.SHOW_MENU_MOBILE, show : show}
)
export const showBalanceDropdown = (show : boolean): UserAction => (
    {type : UserActionTypes.SHOW_BALANCE_DROPDOWN, show : show}
)
export const showLogin = (show : boolean): UserAction => (
    { type : UserActionTypes.SHOW_LOGIN, show : show}
)
export const showPushPrompt = (show : boolean): UserAction => (
    { type : UserActionTypes.SHOW_PUSH_PROMPT, show : show}
)
export const loginRequest = (request : ManualLoginRequest) : UserAction => {
    // let homescreen:boolean = (window.matchMedia('(display-mode: fullscreen)').matches || window.matchMedia('(display-mode: standalone)').matches);
    let updatedReq = request;
    // updatedReq.homescreen = homescreen;
    return {type: UserActionTypes.LOGIN_REQUEST, request: updatedReq}
}

export const mfaRequired = (provider : MultiFactorAuthProvider, username? : string, password? : string) => {
    return { type : UserActionTypes.MFA_VERIFICATION, provider  : provider, username : username, password : password};
}
export const mfaFailed = () => {
    return { type :  UserActionTypes.MFA_VERIFICATION_ERROR };
}
export const resetMfa = () => {
    return { type : UserActionTypes.MFA_RESET }
}

export const termsAcceptRequired = (versionToAccept: string, request: ManualLoginRequest) => {
    return { type : UserActionTypes.TERMS_VERSION_ACCEPT_REQUIRED, versionToAccept: versionToAccept, request: request }
}

export const termsAccepted = () => {
    return  async (dispatch: Dispatch, getState: () => AppState) => {
        const user = getState().user
        let loginRequest = {...user.manualLoginRequest, termsVersionToAccept: user.versionToAccept}
        dispatch(login(loginRequest) as any);
    }
}

export const termsDeclined = () => {
    return {type: UserActionTypes.TERMS_VERSION_DECLINED}
}


export const loginResponse = (response : UserInfo) : UserAction => {
    response.loggedIn && response.userUid && onUserLogin(response.userUid)
    return { type: UserActionTypes.LOGIN_RESPONSE, response: response }
}

export const loginError = (error : LoginError) : UserAction => (
    {type: UserActionTypes.LOGIN_ERROR, error: error}
)
export const logout = () => {
    const token = localStorage.getItem("token");
    const salt = localStorage.getItem("salt");
    const refId = localStorage.getItem("tokenRefId");

    return  async (dispatch : Dispatch) => {
        let userService : UserService = getContainer().getUserService();
        if(token && salt && refId) {
            try {
                await userService.clearUserLoginToken(Number(refId))
            } catch (error) {
                console.warn("Warning error while deleting token",error);

            } finally {
                localStorage.removeItem("token");
                localStorage.removeItem("salt");
                localStorage.removeItem("tokenRefId");
                localStorage.removeItem('LAST_VISITED_ROUTE');
            }
        }

        try {
            await userService.logout();

            if ('credentials' in navigator) {
                if(!localStorage.getItem("disableCredentials")) {
                    const nav:any = navigator;
                    nav.credentials?.preventSilentAccess?.();
                }
            }
        } catch (e) {
            console.log("Exception when trying to logout", e);
        }

        dispatch({type: UserActionTypes.LOGOUT });
    }
}

export const fetchAllUserSettings = () => {
    return async (dispatch : Dispatch) => {
        dispatch({ type : UserActionTypes.USERSETTINGS_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            let value = await userService.getAllUserSettings();
            dispatch({ type : UserActionTypes.USERSETTINGS_RESPONSE, response : value });
        } catch (e) {
            dispatch({ type : UserActionTypes.USERSETTINGS_ERROR, error : e});
        }
    }
}



export const fetchBalance = (login : boolean = false, handleUnavailable : boolean = false, currencies?: String[], skipBalance: boolean = false) => {
    return async (dispatch : Dispatch, getState : () => AppState) => {
        dispatch({ type : UserActionTypes.BALANCE_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            let balances:BalanceAndBonus = await userService.fetchBalances(currencies, skipBalance);
            let messages = balances.messages && balances.messages.messages ? balances.messages.messages : [];
            messages.forEach((um : UserMessage) => {
                if(um.read == false) {
                    if(um.meta?.type == UserMessageType.DEPOSIT_COMPLETE) {
                        let data = um.meta.data;
                        let userUid = getState()?.user?.user?.userUid;
                        data && userUid && onUserDeposit(userUid, data.amount, data.currency, data.amountEur, data.firstDeposit, data.provider, data.paymentUid);
                    } else if(um.meta?.type == UserMessageType.WITHDRAW_APPROVED) {
                        let data = um.meta.data;
                        let userUid = getState()?.user?.user?.userUid;
                        data &&userUid && onUserWithdrawApproved(userUid, data.amount, data.currency, data.amountEur, data.provider, data.paymentUid);
                    }
                }
            });
            dispatch({ type : UserActionTypes.BALANCE_RESPONSE, login: login, response : {
                    messages : messages,
                    unreadMessages : balances.messages?.unreadMessages,
                    bonusOffers:balances.activeOfferList,
                    activeBonuses:balances.activeBonusList,
                    balances : balances.accountBalanceList
            }});
            if(balances.activeBonusList) {
                dispatch({type: BonusActionTypes.ACTIVE_BONUSES_RESPONSE, activeBonuses:balances.activeBonusList})
            }
            if(balances.currencyDefinitionList) {
                dispatch({ type : SettingsActionTypes.UPDATE_CURRENCIES, payload : balances.currencyDefinitionList})
            }
            if(balances.activeOfferList) {
                dispatch({type: BonusActionTypes.BONUS_OFFERS_RESPONSE, offers:balances.activeOfferList});
            }
        } catch (e: any) {
            console.log("ERROR FETCHING BALANCES",e);
            if(e.response?.status == 401) {
                let userService : UserService = getContainer().getUserService();
                await userService.logout();
                dispatch({type : UserActionTypes.LOGOUT});
            } else if(e.response?.status == 503 && handleUnavailable === true) {
                document.location?.reload();
            } else {
                dispatch({ type : UserActionTypes.BALANCE_ERROR, error : e})
            }
        }
    }
}
export const fetchUserInfo = (login : boolean = false) => {
    return async (dispatch : Dispatch, getState : () => AppState) => {
        let loggedInBefore = getState().user?.user?.loggedIn;
        dispatch({type : UserActionTypes.USER_INFO_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            await userService.fetchUserInfo().then((userInfo) => {
                if(userInfo.loggedIn !== false) {
                    userInfo.loggedIn = true;
                    dispatch(fetchBalance(login) as any);
                }
                dispatch({type : UserActionTypes.USER_INFO_RESPONSE, info : userInfo});
                if(!getState().settings?.country && userInfo.country) {
                    dispatch(updateCountry(userInfo.country));
                }
            });
        } catch (e) {
            dispatch({ type : UserActionTypes.USER_INFO_ERROR, error : e});
        }
    }
}


export const updateUserDetails = (attributes : {[key:string] : string}) => {
    return async (dispatch : Dispatch) => {
        let userService = getContainer().getUserService();
        // if(!attributes.screenName) {
        //     throw new SubmissionError({_error : "forms.screenName.invalid"})
        // }
        try {
            let resp = await userService.updateUser(attributes);
            dispatch({type : UserActionTypes.USER_INFO_RESPONSE, info : resp})
        } catch (e: any) {
            dispatch({type: UserActionTypes.USER_INFO_ERROR, error:Error})
            if(e.message === "Request failed with status code 409") {
                throw new SubmissionError({_error : t("forms.ScreenNameAlreadyExist")});
            } else {
                throw new SubmissionError({
                    _error : e.message
                });
            }
        }
    }
}
export const updateUserSettings = (key: string, value: string) => {
    return async (dispatch : Dispatch) => {
        // console.log("updateUserSettings ", value);
        let userService = getContainer().getUserService();
        try {
            // console.log("updateUserSettings 2");
            let resp = await userService.updateUserSettings(key, value);
            // console.log("updateUserSettings 3", resp);
        } catch (e) {
            console.error(e);
        }
    }
}

function getEmailDomain(request: RegistrationRequest) {
    let email = request.email;
    if(!email) {
        return "";
    }
    if(email.indexOf("@") != -1) {
        return email.substr(email.indexOf("@"));
    }
    return "";
}
export class FormSubmissionError extends Error {
    field? : string;
    constructor(msg : string, field?: string) {
        super(msg);
        this.field = field;
    }
}


export const registerAccount = async (request: RegistrationRequest, settings? : Settings) : Promise<RegistrationResponse> => {

    let userService : UserService = getContainer().getUserService();
    let response  : RegistrationResponse;
    let captchaToken : string | undefined;

    if(isReCaptchaActionEnabled(ReCaptchaAction.register, settings)) {
        try {
            captchaToken = await executeCaptcha(ReCaptchaAction.register, settings?.reCaptchaSettings?.siteKey);
        } catch (e) {
            throw new FormSubmissionError(t("user.invalid-recapcha"));
        }
    }

    try {
        response = await userService.register(request, captchaToken);
    } catch (e : any) {
        console.error("error creating account", e);
        if(e.message === "Request failed with status code 429") {
            throw new FormSubmissionError(t("forms.verifyEmail.errorCode429"));
        }else if(e.response && e.response.data === "invalid captcha") {
            throw new FormSubmissionError(t("user.invalid-recapcha"));
        }else {
            throw new FormSubmissionError(e.message);
        }
    }

    if(response.status == Status.SUCCESS) {
        response.userInfo && response.userInfo.userUid && localStorage.setItem("userId", response.userInfo.userUid)
        response.userInfo?.userUid && onUserRegistered(response.userInfo.userUid);
        //Add acquisition Script!
        if (settings?.acquisitionScript && settings?.acquisitionScript.length > 0) {
            try {
                let acquisitionScripts = settings.acquisitionScript.split("\n");
                if (acquisitionScripts.length > 0) {
                    acquisitionScripts.forEach(url => {
                        let acquisitionScript = document.createElement('script');
                        acquisitionScript.src = url;
                        document.body.appendChild(acquisitionScript);
                    })
                }
            } catch (e) {
                console.log(e);
            }
        }

    }else if(response.status == Status.SCREEN_NAME_EXIST) {
        throw new FormSubmissionError(t("user.screenname-taken"));
    } else if(response.status === Status.INVALID_PROMO_CODE) {
        throw new FormSubmissionError(t("user.invalid-promo-code"), response.field);
    } else if(response.status == Status.EMAIL_EXIST) {
        throw new FormSubmissionError(t("user.email-exists"), response.field);
    } else if(response.status == Status.PHONE_EXIST) {
        throw new FormSubmissionError(t("user.phone-exists"), response.field);
    } else if(response.status == Status.UNDER_AGE) {
        throw new FormSubmissionError(t("forms.under-age"), response.field);
    } else if(response.status == Status.BLOCKED_DOMAIN) {
        throw new FormSubmissionError(t("user.email-domain-blocked"), response.field);
    } else if(response.status == Status.INVALID_CHARACTER){
        throw new FormSubmissionError(t("user.email-invalid-character"), response.field);
    } else if(response.status == Status.BLOCKED_IP){
        throw new FormSubmissionError( t("user.invalid-registration-blockedip"), response.field);
    } else if(response.status == Status.BLOCKED_COUNTRY){
        throw new FormSubmissionError(t("user.invalid-registration-blockedcountry"), response.field);
    } else if(response.status == Status.PHONE_INVALID_FORMAT){
        throw new FormSubmissionError(t("forms.invalid-phone-number"), response.field);
    } else if(response.status == Status.DENIED){
        throw new FormSubmissionError(t("forms.denied"), response.field);
    }  else {
        throw new FormSubmissionError(t("user.invalid-registration-details"), response.field);
    }
    return response;
};

export const tokenLogin = (request : TokenLoginRequest) => {
    return async (dispatch : Dispatch, getState : () => AppState ) : Promise<any> => {

        const appMode = getState().user.appMode
        const device = deviceDetect(window.navigator.userAgent ? window.navigator.userAgent : "");
        let userService : UserService = getContainer().getUserService();
        //get fingerprint and add it to amplitude login request;
        let fpID:string = "";
        try {
            fpID = (await window.fingerprint.get()).visitorId
        } catch (e) {
            console.log("Failed to get fingerprint id")
        }
        try {
            let updatedReq = request;
            updatedReq.appMode = appMode

            updatedReq.device = device.os || device.osName || undefined;
            updatedReq.deviceId = fpID;
            const tokenRef = localStorage.getItem("tokenRefId");
            await userService.loginWithToken(updatedReq).then((res) => {
                if(res.loggedIn === false) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("salt");
                    localStorage.removeItem("tokenRefId");

                    dispatch(loginError(LoginError.INVALID_TOKEN));
                    tokenRef && tokenRef.length > 0 && userService.clearUserLoginToken(Number(tokenRef)).then((re) => {
                        userService.logout();
                    })
                } else {
                    //proceed with login
                    res.loggedIn = true;
                    dispatch(loginResponse(res));
                    if(res.responseStatus === LoginResponseStatus.VERIFICATION_REQUIRED) {
                        dispatch(requestVerifyEmail(request) as any);
                    } else {
                        dispatch(fetchUserInfo(true) as any);
                    }
                }
            }).catch(e=>console.log("failed login with token: ", e));


        } catch (e: any) {
            dispatch(loginError(e));
        }

    }
}

export const login = (request : ManualLoginRequest)  => {

    return async (dispatch : Dispatch, getState : () => AppState ) : Promise<any> => {
        const appContext = getAppContext(getState());
        let captchaToken;
        if(isReCaptchaActionEnabled(ReCaptchaAction.login, getState().settings.settings)) {
            try {
                captchaToken = await executeCaptcha(ReCaptchaAction.login, getState().settings.settings?.reCaptchaSettings?.siteKey);
            } catch (e) {
                throw new Error("Invalid login")
            }
        }


        let homescreen:boolean = (window.matchMedia('(display-mode: fullscreen)').matches || window.matchMedia('(display-mode: standalone)').matches);
        // override manifest. OBS: this will be a problem if native app is included.
        // if(getState().user.isHomeScreen){
        //     homescreen = true;
        // }
        const appMode = getState().user.appMode
        const device = deviceDetect(window.navigator.userAgent ? window.navigator.userAgent : "");

        //get fingerprint and add it to amplitude login request;
        let fpID:string = "";
        let isFingerprintPro = getContainer().getSettingsService().getSettingsData()?.fingerprintEnabled;
        try {
                fpID = (await window.fingerprint.get()).visitorId;

        } catch (e) {
            console.log("Failed to get fingerprint id")
        }

        let userService : UserService = getContainer().getUserService();
        try {
            let updatedReq = request;
            // updatedReq.homescreen = homescreen;
            updatedReq.appMode = appMode;
            updatedReq.device = device.os || device.osName || undefined;
            updatedReq.deviceId = fpID;

            if(updatedReq.email && updatedReq.email?.trim().length > 0 ) {
                updatedReq.verification = LoginVerification.EMAIL;
            } else {
                updatedReq.verification = LoginVerification.PHONE;
            }

            let response:UserInfo = await userService.login(updatedReq, captchaToken, appContext);

            if(response.userUid) {
                localStorage.setItem("userId", response.userUid);
                if(isFingerprintPro) {
                    try {
                     window.fingerprintPro.get({tag:{uid:response.userUid }});
                    } catch(e){
                        console.log("Failed to get fingerprintpro!");
                    }
                }

            }

            if((request.email || request.phoneNumber) && request.password && (response.responseStatus == LoginResponseStatus.MULTI_FACTOR_AUTH_REQUIRED)) {
                if(Object.keys(MultiFactorAuthProvider).some(k => k == response.errorMessage)) {
                    dispatch(mfaRequired(response.errorMessage as MultiFactorAuthProvider, request.email, request.password));
                } else {
                    dispatch(mfaFailed());
                }
            } else if((request.email || request.phoneNumber) && request.password && response.responseStatus == LoginResponseStatus.MULTI_FACTOR_AUTH_FAILED) {
                dispatch(mfaFailed())
            } else if(response.responseStatus == LoginResponseStatus.ACCEPT_TERMS_REQUIRED) {
                const versionToAccept = response.responseContext ?? ""
                dispatch(termsAcceptRequired(versionToAccept, updatedReq))
            } else if(response.responseStatus === LoginResponseStatus.MANUAL_APPROVAL_REQUIRED) {
                dispatch(manualApprovalRequired());
            } else if(response.loggedIn === false) {
                throw new Error(response.errorMessage)
            } else {
                let settings = getState().settings.settings;
                let loginsRequired = settings && settings.loginsRequiredForPushPrompt != undefined ? settings.loginsRequiredForPushPrompt : null;
                let pushEnabled:boolean = !!(settings && settings.pushNotificationsEnabled);
                let noOfLoggins = response.userSettings && response.userSettings["noOfLogin"];
                let optOutPushPrompt = response.userSettings && response.userSettings["optOutPushPrompt"];

                if(pushEnabled && noOfLoggins != null && loginsRequired != null && (optOutPushPrompt == null)) {
                    if((Number(noOfLoggins)+1) >= Number(loginsRequired)) {
                        try {
                            //Check if already subscribed
                            if(navigator && navigator.serviceWorker && navigator.serviceWorker.controller) {
                                await navigator.serviceWorker.ready.then((swRegistration:ServiceWorkerRegistration) => {
                                    if(swRegistration) {
                                        swRegistration.pushManager.getSubscription().then((res) => {
                                            if(res == null) {
                                                swRegistration.pushManager.permissionState({userVisibleOnly:true}).then((res) => {
                                                    if(res == "prompt") {
                                                        //browser is set to prompt for subscription means that the user has not yet subscribed.
                                                        dispatch(showPushPrompt(true));
                                                    }
                                                });
                                            }
                                        }).catch((e) => {
                                            console.log("Failed to get subscription: ", e);
                                        })
                                    }
                                }).catch((e) => {
                                    console.log("Service worker not ready: ", e);
                                })
                            }
                        } catch (e) {
                            console.log("Failed to check if user is subscribed: ", e);
                        }
                    }
                }
                // Set token in localstorage, if remember me is checked on login.
                if(response.token && response.secret && response.tokenRefId) {
                    localStorage.setItem("token", response.token);
                    localStorage.setItem("salt", response.secret);
                    localStorage.setItem("tokenRefId", response.tokenRefId.toString());
                    // userService.clearUserLoginToken()
                }

                if(response.responseStatus === LoginResponseStatus.VERIFICATION_REQUIRED) {
                    let newDevice = false;
                    if(response.emailVerified === true && response.verificationChannel == LoginVerification.EMAIL) {
                        newDevice = true;
                    } else if(response.phoneNumberVerified === true && response.verificationChannel == LoginVerification.PHONE) {
                        newDevice = true;
                    }
                    dispatch(requestVerifyEmail(request, newDevice) as any);
                }  else {
                    //proceed with login
                    response.loggedIn = true;
                    dispatch(loginResponse(response));
                    console.log("fetching user info!")
                    dispatch(fetchUserInfo(true) as any);
                    // settings != undefined && newUser != undefined && dispatch(redirectUser(settings, newUser) as any);
                }
            }
        } catch (e: any) {
            dispatch(loginError(e));
            if(e.message === "Request failed with status code 429") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode429")})
            }
            if(e.message === "Request failed with status code 500") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode500")})
            }else if(e.response && e.response.data === "invalid captcha") {
                throw new SubmissionError({_error : t("user.invalid-recapcha")})
            } else {
                throw new SubmissionError({
                    _error: e.message
                });
            }


        }

    }
}
export const manualApprovalRequired = () => {
    return { type : UserActionTypes.MANUAL_APPROVAL_REQUIRED}
}
export const clearManualApprovalRequired = () => {
    return { type : UserActionTypes.CLEAR_MANUAL_APPROVAL}
}
export const requestVerifyEmail = (request : ManualLoginRequest, newDevice?: boolean) => {
    return async (dispatch : Dispatch, getState : () => AppState) : Promise<any> => {

        let userService : UserService = getContainer().getUserService();

        let captchaToken : string | undefined;

        if(isReCaptchaActionEnabled(ReCaptchaAction.requestPrincipalVerification, getState().settings.settings)) {
            try {
                captchaToken = await executeCaptcha(ReCaptchaAction.requestPrincipalVerification, getState().settings.settings?.reCaptchaSettings?.siteKey);
            } catch (e) {
                throw new FormSubmissionError(t("user.invalid-recapcha"));
            }
        }

        try {
            dispatch({ type : UserActionTypes.VERIFY_EMAIL_REQUEST, newDevice : newDevice});
            let principal = request.verification == LoginVerification.EMAIL ? request.email : request.phoneNumber;
            let response = await userService.requestVerificationPrincipal(principal ?? "", request.verification ?? LoginVerification.EMAIL, captchaToken);
            dispatch({ type : UserActionTypes.VERIFY_EMAIL_RESPONSE, request: request });
        } catch (e: any) {
            console.error("Request verify email error", e);
            dispatch({ type : UserActionTypes.VERIFY_EMAIL_ERROR, error : Error });

            if(e.message === "Request failed with status code 429") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode429")})
            } else {
                throw new SubmissionError({
                    _error: e.message
                });
            }
        }
    }
}

export const verifyChannelCode = (manualLoginRequest: ManualLoginRequest, userUid?: string, code?: string) => {
    return async (dispatch : Dispatch) : Promise<any> => {

        let userService : UserService = getContainer().getUserService();
        try {
            dispatch({ type : UserActionTypes.VERIFY_CHANNEL_CODE_REQUEST });

            let response = await userService.verifyChannelCode(userUid != undefined ? userUid : "-1", code != undefined ? code : "0");

            dispatch({ type : UserActionTypes.VERIFY_CHANNEL_CODE_RESPONSE });
            let loginResponse = await login(manualLoginRequest);
            dispatch(loginResponse as any);
        } catch (e: any) {
            console.error("Request verify email error", e);

            dispatch({ type : UserActionTypes.VERIFY_CHANNEL_CODE_ERROR, error : Error });

            if(e.message === "Request failed with status code 406") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode406")});
            } else if(e.message === "Request failed with status code 500") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode500")});
            } else if(e.message === "Request failed with status code 429") {
                throw new SubmissionError({_error : t("forms.verifyEmail.errorCode429")})
            }
            throw new SubmissionError({
                _error: e.message
            });
        }
    }
}


export const resetPassword = (requst: ResetPasswordRequest) => {



    return async (dispatch : Dispatch) : Promise<any> => {
        dispatch({type : UserActionTypes.RESET_PASSWORD_REQUEST, request: requst});
        let userService : UserService = getContainer().getUserService();
        try {
            let response = await userService.resetPassword(requst);
            dispatch({type : UserActionTypes.RESET_PASSWORD_RESPONSE, response: response});
        } catch (error: any) {
            const status = error.response.status
            let msg

            switch(status) {
                case 403: msg = t("forms.resetPassword.errorBadCode"); break;
                case 406: msg = t("forms.resetPassword.errorExpiredCode"); break;
                default: msg = t("forms.resetPassword.error");
            }


            dispatch({ type : UserActionTypes.RESET_PASSWORD_ERROR, error : Error });
            throw new SubmissionError({
                _error: msg
            });
        }
    }
}

export const setShowBalance = (show: boolean) => {
    return (dispatch: Dispatch) =>  {
            dispatch({ type: UserActionTypes.SET_SHOW_BALANCE, show: show })
            // Whenever the balance is hidden, we stop the balance update as its not relevant.
            show ? dispatch(setBalanceUpdateFrequency(defaultBalanceUpdateFrequency) as any) : dispatch(stopBalanceUpdate() as any);
    };
}

export const setActiveCurrency = (currency : string): UserAction => {
    try {
        localStorage.setItem(LAST_SELECTED_CURRENCY_KEY, currency)
    } catch (e) {
        console.log("Unable to write to local storage", e);
    }
    return { type : UserActionTypes.SET_ACTIVE_CURRENCY, activeCurrency : currency};
}

export const resetVerifyProcess = (): UserAction => (
    {type : UserActionTypes.VERIFY_RESET}
)

export const fetchUserLevelView = () => {
    return async (dispatch : Dispatch) => {
        dispatch({ type : UserActionTypes.LEVEL_VIEW_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            let value : UserLevelView = await userService.fetchUserLevelView();
            dispatch({ type : UserActionTypes.LEVEL_VIEW_RESPONSE, response : value});
        } catch (e) {
            console.log("Failed to fetch UserLevelView:",e);
        }
    }
}


export const getUpComingCashbackForUser = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type : UserActionTypes.CASHBACK_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            let response: NextUpcomingCashback[] = await userService.getNextUpcomingCashbackForUser();
            dispatch({ type : UserActionTypes.CASHBACK_RESPONSE, response: response});
            
        } catch (error) {
            console.log(error);
            dispatch({ type : UserActionTypes.CASHBACK_ERROR, error: "failed to fetch user cashback"});
        }
    }
}

export const getUpComingCashbackForPublic = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type : UserActionTypes.CASHBACK_REQUEST});
        try {
            let userService : UserService = getContainer().getUserService();
            let response: NextUpcomingCashback[] = await userService.getNextUpcomingCashbackForPublic();
            dispatch({ type : UserActionTypes.CASHBACK_RESPONSE, response: response});
        } catch (error) {
            console.log(error);
            dispatch({ type : UserActionTypes.CASHBACK_ERROR, error: "failed to fetch user cashback"});
        }
    }
}